import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { apiGet } from '../admin/primitives.js'
import { estErreurApi, type CommunicationOverview, type CommunicationPeriod } from './lib.js'

/**
 * DONNÉES DE LA HOME — un seul appel `/overview` partagé par tous les
 * widgets (le layout engine rend chaque tuile indépendante ; sans ce
 * provider, sept tuiles feraient sept fois la même requête).
 *
 * Trois états distincts, jamais confondus (§126) : chargement ≠ non
 * connecté ≠ erreur. Un ERROR ici est un problème de GESTION (API down) —
 * un provider non connecté arrive, lui, dans `data` avec connected=false.
 */

interface CommData {
  periode: CommunicationPeriod
  setPeriode: (p: CommunicationPeriod) => void
  data: CommunicationOverview | null
  erreur: string | null
  loading: boolean
  reload: () => void
}

const Ctx = createContext<CommData | null>(null)

export function useCommunication(): CommData {
  const v = useContext(Ctx)
  if (!v) throw new Error('useCommunication hors de CommunicationDataProvider')
  return v
}

export function CommunicationDataProvider({ children }: { children: ReactNode }) {
  const [periode, setPeriode] = useState<CommunicationPeriod>('THIS_WEEK')
  const [data, setData] = useState<CommunicationOverview | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const charger = useCallback(async (p: CommunicationPeriod) => {
    setLoading(true)
    try {
      const j = await apiGet<CommunicationOverview | { error: string; message?: string }>(
        `/api/v1/communication/overview?period=${p}`)
      if (estErreurApi(j)) {
        setErreur(j.message ?? 'Lecture impossible.')
      } else {
        setData(j); setErreur(null)
      }
    } catch {
      setErreur('Gestion n’a pas répondu — nouvel essai possible.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void charger(periode) }, [periode, charger])

  return (
    <Ctx.Provider value={{
      periode, setPeriode, data, erreur, loading,
      reload: () => { void charger(periode) },
    }}>
      {children}
    </Ctx.Provider>
  )
}
